import { ScheduledJob, StyleIdDataAggregated } from "@/types/common.types";
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerTable } from "../../common/Table";
import Text from "../../common/Text";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { useJobStyleDataAggregatedQuery } from "@/hooks/queries/useJobStyleDateAggregatedQuery";
import { VirtualTable } from "@/components/common/VirtualTable";
import { PAGE_SIZE } from "@/lib/constants";

type JobsDataTableAggregatedProps = {
  job: ScheduledJob;
  onStyleIdClick: (styleId: StyleIdDataAggregated) => void;
  search: string;
  setSearch: (search: string) => void;
  debouncedSearchValue: string;
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
  isDownloading: boolean;
  onDownload: () => void;
};
const JOB_STYLE_ID_DATA_TABLE_COLUMNS_LABELS = {
  styleId: "Style ID",
  "images[0]": "Image 1",
  articleType: "Article Type",
  averageRating: "Avg. Rating",
  baseColor: "Color",
  brand: "Brand",
  collar: "Collar",
  averageDiscountedPrice: "Avg. Discounted Price",
  averageMrp: "Avg. MRP",
  fabric: "Fabric",
  fabric2: "Fabric 2",
  materialAndCare: "Material & Care",
  fit: "Fit",
  gender: "Gender",
  isFastFashion: "Fast Fashion",
  length: "Length",
  mainTrend: "Main Trend",
  masterCategory: "Master Category",
  occasion: "Occasion",
  season: "Season",
  sleeveLength: "Sleeve Length",
  subCategory: "Sub Category",
  year: "Year",
};
const JobStyleIdDataTable: React.FC<JobsDataTableAggregatedProps> = ({
  job,
  onStyleIdClick,
  search,
  setSearch,
  debouncedSearchValue,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useJobStyleDataAggregatedQuery(
    job.id.toString(),
    PAGE_SIZE,
    debouncedSearchValue
  );

  const { sendEvent } = useAnalyticsManager();
  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("style_sku_search_initiated", {
        search_query: debouncedSearchValue,
      });
    }
  }, [debouncedSearchValue]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const jobDataColumns: ColumnDef<StyleIdDataAggregated>[] = useMemo(
    () => [
      {
        accessorKey: "styleId",
        header: "Style ID",
        cell: ({ row }) => (
          <div
            className="cursor-pointer text-left capitalize text-brand-accent underline"
            onClick={() => onStyleIdClick(row.original)}
          >
            {row.original.styleId}
          </div>
        ),
      },
      {
        accessorKey: "images[0]",
        header: "Image 1",
        cell: ({ row }) => (
          <img
            src={row.original?.images[0]}
            alt="Product"
            className="h-14 w-16 rounded-none object-cover text-left"
          />
        ),
      },
      {
        accessorKey: "articleType",
        header: "Article Type",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.articleType}
          </div>
        ),
      },

      {
        accessorKey: "averageRating",
        header: "Avg. Rating",
        cell: ({ row }) => (
          <div>{row.original?.averageRating?.toFixed(1) || "N/A"}</div>
        ),
      },
      {
        accessorKey: "baseColor",
        header: "Color",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.baseColor}</div>
        ),
      },
      {
        accessorKey: "brand",
        header: "Brand",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.brand}</div>
        ),
      },
      {
        accessorKey: "collar",
        header: "Collar",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.collar || "N/A"}
          </div>
        ),
      },

      {
        accessorKey: "averageDiscountedPrice",
        header: "Avg. Discounted Price",
        cell: ({ row }) => (
          <div>₹{row.original.averageDiscountedPrice?.toFixed(2) || "N/A"}</div>
        ),
      },
      {
        accessorKey: "averageMrp",
        header: "Avg. MRP",
        cell: ({ row }) => (
          <div>₹{row.original.averageMrp?.toFixed(2) || "N/A"}</div>
        ),
      },
      {
        accessorKey: "fabric",
        header: "Fabric",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.fabric}</div>
        ),
      },
      {
        accessorKey: "fabric2",
        header: "Fabric 2",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.fabric2}</div>
        ),
      },
      {
        accessorKey: "materialAndCare",
        header: "Material & Care",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.materialAndCare || "N/A"}
          </div>
        ),
      },
      {
        accessorKey: "fit",
        header: "Fit",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.fit}</div>
        ),
      },
      {
        accessorKey: "gender",
        header: "Gender",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.gender}</div>
        ),
      },

      {
        accessorKey: "isFastFashion",
        header: "Fast Fashion",
        cell: ({ row }) => (
          <div className="text-left">
            {row.original.isFastFashion ? "Yes" : "No"}
          </div>
        ),
      },
      {
        accessorKey: "length",
        header: "Length",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.length}</div>
        ),
      },
      {
        accessorKey: "mainTrend",
        header: "Main Trend",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.mainTrend}</div>
        ),
      },

      {
        accessorKey: "masterCategory",
        header: "Master Category",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.masterCategory}
          </div>
        ),
      },

      {
        accessorKey: "occasion",
        header: "Occasion",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.occasion}</div>
        ),
      },

      {
        accessorKey: "season",
        header: "Season",
        cell: ({ row }) => (
          <div className="text-left capitalize">{row.original.season}</div>
        ),
      },

      {
        accessorKey: "sleeveLength",
        header: "Sleeve Length",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.sleeveLength || "N/A"}
          </div>
        ),
      },
      {
        accessorKey: "subCategory",
        header: "Sub Category",
        cell: ({ row }) => (
          <div className="text-left capitalize">
            {row.original.subCategory}
          </div>
        ),
      },
      {
        accessorKey: "year",
        header: "Year",
        cell: ({ row }) => (
          <div className="text-left">{row.original.year}</div>
        ),
      },
    ],
    []
  );
  const tableData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data.map((item) => item.pdpData));
  }, [data?.pages]);

  const table = useReactTable({
    data: tableData,
    columns: jobDataColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    // onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnVisibility,
    },
  });

  const loadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);
  // const handlePageChange = (page: number) => {
  //   setPagination((prev) => ({
  //     ...prev,
  //     pageIndex: page - 1,
  //   }));
  // };

  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            JOB_STYLE_ID_DATA_TABLE_COLUMNS_LABELS[
              column.id as keyof typeof JOB_STYLE_ID_DATA_TABLE_COLUMNS_LABELS
            ] || column.id,
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);

  if (isLoading) {
    return (
      <JobDataHeader search={search} setSearch={setSearch} totalJobs={0}>
        <ShimmerTable<StyleIdDataAggregated>
          columns={jobDataColumns}
          rowCount={5}
        />
      </JobDataHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobDataHeader
        search={search}
        setSearch={setSearch}
        totalJobs={data?.pages[0]?.totalElements || 0}
        table={table}
      >
        <VirtualTable
          table={table}
          loadMore={loadMore}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          columns={jobDataColumns} // Pass columns prop
        />
      </JobDataHeader>
    </>
  );
};

export default JobStyleIdDataTable;

type JobDataHeaderProps = {
  search: string;
  setSearch: (search: string) => void;
  totalJobs?: number;
  children: React.ReactNode;
  table?: ReturnType<typeof useReactTable<StyleIdDataAggregated>>;
};

const JobDataHeader: React.FC<JobDataHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      <div className="mb-3 flex items-center justify-between"></div>
      {totalJobs && totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
